import { useCallback, useMemo } from 'react';

import { AxiosRequestConfig } from 'axios';
import useAPI from 'hooks/use-api';
import { API, request } from 'redux/api';

interface ClassAttendanceDetailsResponse extends ClassAttendanceDetails, MessageResponse {}

export const useClassAttendanceAPI = (courseId = '', classId = '') => {
  const config = useMemo<AxiosRequestConfig>(
    () => ({
      url: API.ATTENDANCE_CLASS,
      params: { courseId, classId },
    }),
    [classId, courseId]
  );

  return useAPI<ClassAttendanceDetailsResponse, MessageResponse>(config, Boolean(courseId && classId));
};

interface IndividualAttendanceData extends IndividualAttendance {
  type: 'individual';
}

interface GetIndividualAttendanceResponse extends IndividualAttendance, MessageResponse {}

export const useIndividualAttendanceAPI = async (courseId = '', studentId = '') => {
  const transformResponse = useCallback(
    (response: GetIndividualAttendanceResponse) => ({ ...response, type: 'individual' }),
    []
  );

  const config = useMemo<AxiosRequestConfig>(
    () => ({
      url: API.ATTENDANCE_STUDENT,
      params: { courseId, studentId },
      transformResponse,
    }),
    [courseId, studentId, transformResponse]
  );

  return useAPI<IndividualAttendanceData, MessageResponse>(config, Boolean(courseId && studentId));
};

interface AggregateAttendanceData extends AggregateAttendance {
  type: 'aggregate';
}

interface GetAggregateAttendanceResponse extends AggregateAttendance, MessageResponse {}

export const useAggregateAttendanceAPI = (studentId = '') => {
  const transformResponse = useCallback(
    (response: GetAggregateAttendanceResponse) => ({ ...response, type: 'aggregate' }),
    []
  );

  const config = useMemo<AxiosRequestConfig>(
    () => ({
      url: API.ATTENDANCE_ADMIN_STUDENT,
      params: { studentId },
      transformResponse,
    }),
    [studentId, transformResponse]
  );

  return useAPI<AggregateAttendanceData, MessageResponse>(config, Boolean(studentId));
};

export const useStudentAttendanceDetailsAPI = (getAccess: SessionData | null, studentId = '') => {
  const transformResponse = useCallback(
    (response: GetIndividualAttendanceResponse | GetAggregateAttendanceResponse) => ({
      ...response,
      type: getAccess?.context === 'course' ? 'individual' : 'aggregate',
    }),
    [getAccess]
  );

  const config = useMemo<AxiosRequestConfig>(() => {
    if (getAccess?.context === 'course') {
      return {
        url: API.ATTENDANCE_STUDENT,
        params: { courseId: getAccess.courseDetails.courseId, studentId },
        transformResponse,
      };
    }
    return {
      url: API.ATTENDANCE_ADMIN_STUDENT,
      params: { studentId },
      transformResponse,
    };
  }, [getAccess, studentId, transformResponse]);

  const shouldFetch = useMemo(() => {
    if (getAccess?.context === 'course') {
      return Boolean(studentId && getAccess.courseDetails.courseId);
    }
    return Boolean(studentId);
  }, [getAccess, studentId]);

  return useAPI<IndividualAttendanceData | AggregateAttendanceData, MessageResponse>(config, shouldFetch);
};

interface ActivityScoreResponse extends MessageResponse {
  activities: ActivityScore[];
}

export const useActivityScoreAPI = () => {
  const transformResponse = useCallback((response: ActivityScoreResponse): ActivityScoreMap => {
    const activityScoreMap: ActivityScoreMap = {
      quiz: [],
      poll: [],
      discussion: [],
      class: [],
      assignment: [],
    };

    response.activities
      .sort((a1, a2) => a1.publishedOn - a2.publishedOn)
      .forEach((a) => {
        activityScoreMap[a.type].push(a);
      });

    return activityScoreMap;
  }, []);

  const config = useMemo<AxiosRequestConfig>(
    () => ({
      url: API.COURSE_ACTIVITY_SCORES,
      transformResponse,
    }),
    [transformResponse]
  );

  return useAPI<ActivityScoreMap, MessageResponse>(config);
};

export interface BulkExportActivities {
  message: MessageResponse['message'];
  activityType: 'quiz' | 'poll';
  activities: BulkActivityScore[];
  /** always 1 for activityType === 'poll' */
  normalize: NumericBoolean;
  /** always non-zero */
  normalizeTo: number;
  exportAsPercentage: NumericBoolean;
  /** number between 0 and 100 */
  latePenalty: number;
  lastExportedOn: UnixTime;
}

const transformBulkExportActivities = (response: BulkExportActivities): BulkExportActivities => {
  const sortedActivities = response.activities.sort((a, b) => a.publishedOn - b.publishedOn);
  return {
    ...response,
    activities: sortedActivities,
  };
};

export const getBulkExportActivities = (activityType: 'quiz' | 'poll') => {
  return request<BulkExportActivities>({
    url: API.COURSE_ACTIVITY_GET_BULK_EXPORT_SCORES,
    params: { activityType },
    method: 'get',
    transformResponse: transformBulkExportActivities,
  });
};

export const useActivityDetailsAPI = <T extends ActivityDetails>(
  activityId = '',
  activityType: ActivityType = 'quiz'
) => {
  const config = useMemo<AxiosRequestConfig>(
    () => ({
      url: API.COURSE_ACTIVITY_DETAILS,
      params: { activityId, activityType },
    }),
    [activityId, activityType]
  );

  return useAPI<MessageResponse & T, MessageResponse>(config, Boolean(activityId && activityType));
};

interface ExportActivityDetailsPayload {
  activityType: ActivityType;
  activityId: string;
  latePenalty: number;
  maxScore: number;
  particulars: {
    userId: string;
    latePenalty: number;
  }[];
}

export const exportActivityDetails = (data: ExportActivityDetailsPayload) => {
  return request<MessageResponse>({
    url: API.COURSE_EXPORT_ACTIVITY_DETAILS,
    method: 'post',
    data,
  });
};

interface ExportBulkActivityScoresPayload {
  activityType: 'quiz' | 'poll';
  activityIds: string[];
  /** always 1 for activityType === 'poll' */
  normalize: NumericBoolean;
  normalizeTo: number;
  exportAsPercentage: NumericBoolean;
  /** number between 0 and 100 */
  latePenalty: number;
}

export const exportBulkActivityScores = (data: ExportBulkActivityScoresPayload) => {
  return request<MessageResponse>({
    url: API.COURSE_ACTIVITY_BULK_EXPORT,
    method: 'post',
    data,
  });
};

interface ExportAttendanceDetailsPayload {
  activityType: 'attendance';
  activityId: string;
  latePenalty: number;
  maxScore: number;
  particulars: {
    userId: string;
    latePenalty: number;
  }[];
}

export const removeExportedActivity = ({
  activityType,
  activityId,
}: {
  activityType: string;
  activityId: string;
}) => {
  return request<MessageResponse>({
    url: API.REMOVE_EXPORTED_ACTIVITY,
    params: { activityType, activityId },
    method: 'delete',
  });
};

export const removeExportedAttendance = ({
  activityType,
  activityId,
}: {
  activityType: string;
  activityId: string;
}) => {
  return request({
    url: API.REMOVE_EXPORTED_ATTENDANCE,
    params: { activityType, activityId },
    method: 'delete',
  });
};

export const exportAttendanceDetails = (data: ExportAttendanceDetailsPayload) => {
  return request<MessageResponse>({
    url: API.ATTENDANCE_EXPORT,
    method: 'post',
    data,
  });
};

interface ExportAllAttendance {
  message: MessageResponse['message'];
  numSessions: number;
  pointsPerSession: number;
  latePenaltyPerc: number;
  excusedIsPresent: NumericBoolean;
  automaticSync: NumericBoolean;
  lastExported: number;
}

export const exportAllAttendance = () => {
  return request<ExportAllAttendance>({
    url: API.EXPORT_ALL_ATTENDANCE,
    method: 'get',
  });
};

export const deleteAllExportedAttendance = () => {
  return request<MessageResponse>({
    url: API.EXPORT_ALL_ATTENDANCE,
    method: 'delete',
  });
};

type OverwriteAllExportedAttendance = Omit<ExportAllAttendance, 'message' | 'numSessions' | 'lastExported'>;

export const overwriteAllExportedAttendance = (data: OverwriteAllExportedAttendance) => {
  return request<MessageResponse>({
    url: API.EXPORT_ALL_ATTENDANCE,
    method: 'put',
    data,
  });
};

export const useStudentAnalyticsAPI = (userId: string) => {
  const config = useMemo<AxiosRequestConfig>(
    () => ({
      url: API.STUDNET_ANALYTICS,
      params: { userId },
    }),
    [userId]
  );
  return useAPI<MessageResponse & StudentAnalytics, MessageResponse>(config, Boolean(userId));
};
